/**
 * World packages, format v1 (content studio part 3a, ADR-049;
 * docs/superpowers/specs/2026-10-07-world-packages-design.md): a world as data, built on the maker's
 * side -- `wereld.json`, GLBs, a height map and optional materials -- that the headset builds from its
 * own building blocks and never runs code from. Pure; imports nothing outside world-package/ but
 * ../stage.js, because tools/wereld-kit carries a copy of this folder.
 */
export const WORLD_FORMAT_VERSIONS = [1, 2];
/** A world's slug, as the code worlds' (`volcanic-island`). */
export const WORLD_SLUG = /^[a-z][a-z0-9-]{1,39}$/;
/**
 * Slugs a world may not take: they name a scene that is not a world (catalogue.ts), and a design
 * chooses scenes by slug. A test in catalogue.test.ts holds this list to the scenes.
 */
export const RESERVED_WORLD_SLUGS = [
    'ar-room',
    'solar-system',
    'studio',
    'pano360',
    'gltf',
    'procedural',
    'world',
    // A brief's place for the school's own 360° photo, and a preset nothing offers (yet).
    'brief-photo',
    'campus',
];
/** A file in the zip: flat, lower case, one of the kinds the format knows. */
export const WORLD_FILE = /^[a-z0-9][a-z0-9_-]{0,59}\.(glb|bin|hdr|jpg|png|ogg)$/;
const KIND_BY_EXTENSION = {
    glb: 'glb',
    bin: 'heights',
    hdr: 'sky',
    jpg: 'texture',
    png: 'texture',
    ogg: 'sound',
};
export function fileKindOf(name) {
    if (!WORLD_FILE.test(name))
        return undefined;
    return KIND_BY_EXTENSION[name.slice(name.lastIndexOf('.') + 1)];
}
export const WORLD_PACKAGE_LIMITS = {
    files: 32,
    glbs: 8,
    fileBytes: 40 * 2 ** 20,
    totalBytes: 100 * 2 ** 20,
    name: 40,
    nameEn: 80,
    description: 280,
    /** The generator's English line: what a world is and what it is for. */
    descriptionEn: 120,
    blocks: 24,
    /** Features (onderdelen), each of 1 to `states` states, and at most `totalStates` in all (21 Oct 2026). */
    features: 9,
    states: 6,
    totalStates: 24,
    /** Old names a package keeps (`eerder` per feature, `eerdereOnderdelen`), each. */
    oldNames: 12,
    heightsSide: { min: 2, max: 2049 },
    /** |x| and |z| of anything placed, metres. */
    extent: 2000,
    creditText: 200,
    creditSource: 500,
    skyWidth: 4096,
    textureSide: 2048,
    soundSeconds: 120,
    /** Settings one state of a feature may change. */
    stateChanges: 64,
    props: 64,
    propPlacements: 400,
    /** Views a world is built around that no model may stand in (houdVrij, ADR-053). */
    keepClear: 4,
    /** The picture of briefs and place cards (ADR-050); at most 3 MB as stored (ADR-059). */
    picture: { minWidth: 640, maxWidth: 1920, maxBytes: 3 * 1024 * 1024 },
};
/** What a package may cost a Quest 3 (spec §3; ADR-032's numbers, the island's triangles). */
export const WORLD_PACKAGE_BUDGET = {
    triangles: 300_000,
    drawCalls: 60,
    textureMB: 96,
    downloadMB: 20,
};
/**
 * A package with a plaatsruimte: the villa's line (apps/headset budget.ts VILLA_BUDGET), since the
 * pupils' work (FURNITURE_COST: 40 pieces of 6,000 triangles) comes on top within a world lesson's total.
 */
export const WORLD_PACKAGE_PLACEMENT_BUDGET = {
    triangles: 110_000,
    drawCalls: 45,
    textureMB: 66,
    downloadMB: 12,
};
/** What the blocks may ask of the headset at run time, whatever their settings (V3). */
export const BLOCK_CAPS = {
    drift: 600,
    puffs: 120,
    sparks: 800,
    flames: 8,
    swarm: 200,
    route: 120,
    routePartTriangles: 2000,
    lights: 2,
    /** What the headset draws per world (look/halos.ts takes it from here): an ash cloud counts two. */
    halos: 16,
    rotating: 16,
};
/** The model library's licences (ADR-043), for every material and GLB. */
export const WORLD_LICENCES = ['cc0', 'cc-by-4.0', 'cc-by-3.0', 'public-domain'];
export const PARTICLE_KINDS = ['zweven', 'rook', 'aswolk', 'vonken', 'vlam', 'stoom'];
export const SWARM_KINDS = ['vlinders', 'vissen'];
export const SHOW_WAYS = ['meteen', 'groeien', 'vervagen'];
export const COLOUR_LOOKS = ['normaal', 'licht'];
export const SURFACE_EFFECTS = ['lava', 'caustics', 'wateroppervlak'];
export const BLOCK_TYPES = [
    'water',
    'grass',
    'birds',
    'surface',
    'deeltjes',
    'zwerm',
    'draaien',
    'zichtbaar',
    'kleur',
    'route',
    'licht',
    'plaatsruimte',
];
export const BLOCK_TYPES_V1 = [
    'water',
    'grass',
    'birds',
    'surface',
];
/** A package's v2 keys, with v1's meaning when it has none (V1). */
export function packageExtras(pkg) {
    return {
        descriptionEn: pkg.descriptionEn ?? pkg.name.en,
        staged: pkg.staged ?? true,
        picture: pkg.picture ?? null,
        props: pkg.props ?? [],
    };
}
//# sourceMappingURL=format.js.map